import { createSharedComposable } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { AppNotification } from '@/modules/Notification/types/model'
import { DEFAULT_PAGE_SIZE } from '@/modules/Core/constants/pagination'
import { useDeleteAllEmployeeNotificationsMutation } from '@/modules/Notification/requests/mutations/useDeleteAllEmployeeNotificationsMutation'
import { useDeleteEmployeeNotificationMutation } from '@/modules/Notification/requests/mutations/useDeleteEmployeeNotificationMutation'
import { useMarkAllEmployeeNotificationsReadMutation } from '@/modules/Notification/requests/mutations/useMarkAllEmployeeNotificationsReadMutation'
import { useMarkEmployeeNotificationReadMutation } from '@/modules/Notification/requests/mutations/useMarkEmployeeNotificationReadMutation'
import { useEmployeeNotificationsPaginatedQuery } from '@/modules/Notification/requests/queries/useEmployeeNotificationsPaginatedQuery'
import { useEmployeeNotificationsUnreadCountQuery } from '@/modules/Notification/requests/queries/useEmployeeNotificationsUnreadCountQuery'
import { mapEmployeeNotificationToApp } from '@/modules/Notification/utils/employeeNotificationMappers'

function useNotificationPanelState() {
  const router = useRouter()
  const isOpen = ref(false)
  const shouldFetchUnreadCount = ref(false)

  const listParams = computed(() => ({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  }))

  const { data, isFetching } = useEmployeeNotificationsPaginatedQuery(
    listParams,
    {
      enabled: computed(() => isOpen.value),
    }
  )

  const notifications = computed(() =>
    (data.value?.items ?? []).map(mapEmployeeNotificationToApp)
  )

  const { data: unreadCountData } = useEmployeeNotificationsUnreadCountQuery({
    enabled: computed(() => shouldFetchUnreadCount.value),
  })

  const unreadCount = computed(() => unreadCountData.value ?? 0)

  function ensureUnreadCountLoaded() {
    shouldFetchUnreadCount.value = true
  }

  const { mutateAsync: markReadAsync } =
    useMarkEmployeeNotificationReadMutation()
  const { mutate: markAllRead } = useMarkAllEmployeeNotificationsReadMutation()
  const { mutate: deleteOne } = useDeleteEmployeeNotificationMutation()
  const { mutate: deleteAll } = useDeleteAllEmployeeNotificationsMutation()

  function open() {
    ensureUnreadCountLoaded()
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  function toggle() {
    if (!isOpen.value) {
      ensureUnreadCountLoaded()
    }
    isOpen.value = !isOpen.value
  }

  function onDeleteAll() {
    deleteAll()
  }

  function onMarkAllRead() {
    markAllRead()
  }

  function onDeleteOne(id: number) {
    deleteOne(id)
  }

  async function onOpenNotification(notification: AppNotification) {
    if (!notification.isRead) {
      try {
        await markReadAsync(notification.id)
      } catch {
        // Still navigate for letter notifications if mark-read fails
      }
    }

    if (notification.type !== 'letter') {
      return
    }

    close()
    await router.push({
      name: 'ManageLettersRoute',
      query: { tab: 'received' },
    })
  }

  return {
    isOpen,
    notifications,
    isFetching,
    unreadCount,
    ensureUnreadCountLoaded,
    open,
    close,
    toggle,
    deleteAll: onDeleteAll,
    markAllRead: onMarkAllRead,
    deleteOne: onDeleteOne,
    openNotification: onOpenNotification,
  }
}

export const useNotificationPanel = createSharedComposable(
  useNotificationPanelState
)
